import SeoFooter from "../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  MapPin, Phone, Mail, Plane, ArrowRight, Car, Clock, Shield, Star
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const CitiesPage = () => {
  const cities = [
    {
      name: "Brisbane",
      code: "BNE",
      slug: "brisbane",
      image: "https://images.unsplash.com/photo-1566734904496-9309bb1798ae?w=600&h=400&fit=crop",
      description: "Queensland's capital city with seamless airport transfers to CBD, Gold Coast, and Sunshine Coast.",
      highlights: ["Brisbane Airport (BNE)", "CBD Hotels", "Gold Coast Connections"]
    },
    {
      name: "Gold Coast",
      code: "OOL",
      slug: "gold-coast",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop",
      description: "Australia's premier beach destination with transfers to theme parks, resorts, and Byron Bay.",
      highlights: ["Gold Coast Airport (OOL)", "Theme Parks", "Byron Bay"]
    },
    {
      name: "Sydney",
      code: "SYD",
      slug: "sydney",
      image: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=600&h=400&fit=crop",
      description: "Australia's largest city with premium transfers to CBD, Northern Beaches, and Blue Mountains.",
      highlights: ["Sydney Airport (SYD)", "CBD & Harbour", "Blue Mountains"]
    },
    {
      name: "Melbourne",
      code: "MEL",
      slug: "melbourne",
      image: "https://images.unsplash.com/photo-1514395462725-fb4566210144?w=600&h=400&fit=crop",
      description: "Victoria's cultural capital with transfers to CBD, Yarra Valley, and Great Ocean Road.",
      highlights: ["Melbourne Airport (MEL)", "CBD Hotels", "Yarra Valley"]
    },
    {
      name: "Cairns",
      code: "CNS",
      slug: "cairns",
      image: "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=600&h=400&fit=crop",
      description: "Gateway to the Great Barrier Reef with transfers to Port Douglas, Palm Cove, and rainforest lodges.",
      highlights: ["Cairns Airport (CNS)", "Port Douglas", "Palm Cove"]
    },
    {
      name: "Perth",
      code: "PER",
      slug: "perth",
      image: "https://images.unsplash.com/photo-1559331709-adc470c83d72?w=600&h=400&fit=crop",
      description: "Western Australia's capital with transfers to CBD, Fremantle, and Margaret River wine region.",
      highlights: ["Perth Airport (PER)", "Fremantle", "Margaret River"]
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Limo Near Me Service Locations",
    "description": "Premium airport transfer services across major Australian cities",
    "numberOfItems": cities.length,
    "itemListElement": cities.map((city, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": `${city.name} Airport Transfers`,
      "url": `https://www.limonearme.com.au/chauffeur/${city.slug}`
    }))
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Service Locations | Airport Transfers Across Australia | Limo Near Me</title>
          <meta name="description" content="Premium airport transfer services in Brisbane, Gold Coast, Sydney, Melbourne, Cairns, and Perth. Professional chauffeurs, luxury vehicles, 24/7 service." />
          <meta name="keywords" content="airport transfers Australia, Brisbane airport transfer, Sydney chauffeur, Melbourne limo, Gold Coast airport, Cairns transfers, Perth airport taxi" />
          <link rel="canonical" href="https://www.limonearme.com.au/cities" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/corporate-travel" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Corporate</Link>
                <Link to="/book" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Book Now</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/login">
                  <Button variant="outline" size="sm">Login</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 mb-6">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">Australia-Wide Service</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Our Service Locations
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
                Premium airport transfers and chauffeur services across Australia's major cities.
              </p>
              <Link to="/book">
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 rounded-full px-8">
                  Book Your Transfer
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Features Bar */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {[
                { icon: Car, text: "Luxury Fleet" },
                { icon: Clock, text: "24/7 Service" },
                { icon: Shield, text: "Licensed Chauffeurs" },
                { icon: Star, text: "5-Star Rated" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-muted-foreground">
                  <item.icon className="h-5 w-5 text-primary" />
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Cities Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cities.map((city, index) => (
                <motion.div
                  key={city.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/chauffeur/${city.slug}`} className="group block">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={city.image} 
                          alt={`${city.name} airport transfers`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="flex items-center gap-2 text-white">
                            <Plane className="h-4 w-4" />
                            <span className="text-sm font-medium">{city.name} Airport ({city.code})</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-6">
                        <h2 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {city.name}
                        </h2>
                        <p className="text-muted-foreground text-sm mb-4">
                          {city.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {city.highlights.map((highlight, i) => (
                            <span 
                              key={i}
                              className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center text-primary font-medium text-sm group-hover:gap-2 transition-all">
                          View {city.name} Services
                          <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Book Your Transfer?</h2>
            <p className="text-primary-foreground/80 mb-8">
              Get an instant quote for airport transfers in any of our service locations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" variant="secondary" className="rounded-full px-8">
                  Get a Quote
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default CitiesPage;
